<template>
  <nav 
    class="navbar-container backdrop-blur-md bg-base-200/70 w-full relative z-50"
    :class="{ 'navbar-scrolled': hasScrolled }"
    ref="navbarContainer"
  >
    <div class="nav-backdrop"></div>
    <div class="nav-accent top-accent"></div>
    <div class="nav-accent bottom-accent"></div>
    
    <!-- Steel corner accents -->
    <div class="corner-accent top-left"></div>
    <div class="corner-accent top-right"></div>
    <div class="corner-accent bottom-left"></div>
    <div class="corner-accent bottom-right"></div>
    
    <!-- Decorative elements inspired by metalwork -->
    <div class="nav-decorative-element left-element"></div>
    <div class="nav-decorative-element right-element"></div>
    
    <!-- Grid Layout Container -->
    <div class="navbar-grid">
      <!-- Left Section - Menu pushed to far left -->
      <div class="navbar-section left-section">
        <!-- Menu Component - Now pushed to far left -->
        <MenuComponent class="menu-component" />
        
        <!-- Left Navigation - Now right-justified against logo -->
        <ul class="nav-list left-nav w-full">
          <li v-for="(section, index) in leftSections" :key="section.id"
              :style="{ 'animation-delay': `${index * 0.1}s` }"
              class="nav-item-container">
            <a
              :href="'#' + section.id"
              class="nav-item group"
              @click.prevent="scrollToSection(section.id)"
            >
              <span class="nav-item-text">{{ section.name }}</span>
              <span class="nav-item-underline"></span>
            </a>
          </li>
        </ul>
      </div>
      
      <!-- Center Logo -->
      <div class="navbar-section logo-section">
        <LogoSTE />
      </div>
      
      <!-- Right Section - Social media pushed to far right -->
      <div class="navbar-section right-section">
        <!-- Right Navigation - Now left-justified against logo -->
        <ul class="nav-list right-nav w-full">
          <li v-for="(section, index) in rightSections" :key="section.id"
              :style="{ 'animation-delay': `${(index + 3) * 0.1}s` }"
              class="nav-item-container">
            <a
              :href="'#' + section.id"
              class="nav-item group"
              @click.prevent="scrollToSection(section.id)"
            >
              <span class="nav-item-text">{{ section.name }}</span>
              <span class="nav-item-underline"></span>
            </a>
          </li>
        </ul>
        
        <!-- Social Media Links - Now pushed to far right -->
        <SocialMediaLinks class="social-links-component" />
      </div>
    </div>
  </nav>
</template>

<script>
import LogoSTE from '@/components/LogoSTE.vue';
import MenuComponent from '@/components/MenuComponent.vue';
import SocialMediaLinks from '@/components/SocialMediaLinks.vue';

export default {
  name: 'NavBar',
  props: {
    sections: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      hasScrolled: false,
      scrollThreshold: 50,
      isAnimating: false,
      observer: null
    }
  },
  computed: {
    leftSections() {
      return this.sections.slice(0, 3);
    },
    rightSections() {
      return this.sections.slice(3);
    }
  },
  mounted() {
    window.addEventListener('scroll', this.handleScroll);
    
    // Initialize intersection observer for entrance animations
    this.setupIntersectionObserver();
    
    // Trigger entrance animation
    setTimeout(() => {
      this.isAnimating = true;
    }, 100);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll);
    if (this.observer) {
      this.observer.disconnect();
    }
  },
  methods: {
    scrollToSection(id) {
      console.log(`NavBar scrollToSection called with id: ${id}`);
      
      // Emit event to parent component
      this.$emit('scroll-to-section', id);
      
      // Note: Removed fallback behavior to prevent conflicts with custom scroll handling
      // Parent components should handle the scroll-to-section event
    },
    handleScroll() {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      this.hasScrolled = scrollTop > this.scrollThreshold;
    },
    setupIntersectionObserver() {
      const options = {
        root: null,
        rootMargin: '0px',
        threshold: 0.1
      };
      
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('in-view');
          }
        });
      }, options);
      
      // Observe navbar container
      if (this.$refs.navbarContainer) {
        this.observer.observe(this.$refs.navbarContainer);
      }
    }
  },
  components: {
    LogoSTE,
    MenuComponent,
    SocialMediaLinks
  },
};
</script>

<style scoped>
:deep(.social-icon) {
  margin: 0 !important;
}

.navbar-container {
  width: 100%;
  height: 60px;
  display: flex;
  z-index: 100;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.25);
  position: relative;
  overflow: visible; /* Changed from hidden to allow logo to overflow */
  transition: all 0.4s cubic-bezier(0.16, 1, 0.3, 1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

/* CSS Grid Layout for navbar */
.navbar-grid {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  width: 100%;
  height: 100%;
  position: relative;
}

/* Navbar sections */
.navbar-section {
  display: flex;
  align-items: center;
  position: relative;
  z-index: 10;
}

.left-section {
  justify-content: space-between; /* Changed from flex-start */
  padding-left: 0; /* Remove left padding */
}

.logo-section {
  justify-content: center;
  position: relative;
}

.right-section {
  justify-content: space-between; /* Changed from flex-end */
  padding-right: 0; /* Remove right padding */
}

/* Component styling */
.menu-component {
  flex-shrink: 0;
  margin-left: 0; /* Ensure menu is at far left */
}

.social-links-component {
  flex-shrink: 0;
  margin-right: 0; /* Ensure social links are at far right */
  margin-left: auto; /* Push to the right edge */
}

/* Glass-morphism backdrop */
.nav-backdrop {
  position: absolute;
  inset: 0;
  background: linear-gradient(90deg, 
    rgba(42, 43, 56, 0.7), 
    rgba(29, 30, 38, 0.85), 
    rgba(42, 43, 56, 0.7)
  );
  z-index: -1;
}

/* Animated accent lines */
.nav-accent {
  position: absolute;
  left: 0;
  height: 1px;
  width: 100%;
  z-index: 1;
}

.top-accent {
  top: 0;
  background: linear-gradient(90deg, 
    transparent 0%, 
    rgba(143, 0, 0, 0.3) 20%, 
    rgba(255, 255, 255, 0.3) 40%, 
    rgba(255, 255, 255, 0.1) 60%, 
    rgba(143, 0, 0, 0.3) 80%, 
    transparent 100%
  );
  animation: slide 8s linear infinite;
}

.bottom-accent {
  bottom: 0;
  background: linear-gradient(90deg, 
    transparent 0%, 
    rgba(143, 0, 0, 0.3) 20%, 
    rgba(255, 255, 255, 0.15) 40%, 
    rgba(255, 255, 255, 0.05) 60%, 
    rgba(143, 0, 0, 0.3) 80%, 
    transparent 100%
  );
  animation: slide-reverse 8s linear infinite;
}

/* Corner accents */
.corner-accent {
  position: absolute;
  width: 15px;
  height: 15px;
  z-index: 2;
  opacity: 0;
  transition: all 0.3s ease-out;
}

.navbar-container.in-view .corner-accent {
  opacity: 1;
}

.top-left {
  top: 0;
  left: 0;
  border-top: 2px solid rgba(143, 0, 0, 0.7);
  border-left: 2px solid rgba(143, 0, 0, 0.7);
}

.top-right {
  top: 0;
  right: 0;
  border-top: 2px solid rgba(143, 0, 0, 0.7);
  border-right: 2px solid rgba(143, 0, 0, 0.7);
}

.bottom-left {
  bottom: 0;
  left: 0;
  border-bottom: 2px solid rgba(143, 0, 0, 0.7);
  border-left: 2px solid rgba(143, 0, 0, 0.7);
}

.bottom-right {
  bottom: 0;
  right: 0;
  border-bottom: 2px solid rgba(143, 0, 0, 0.7);
  border-right: 2px solid rgba(143, 0, 0, 0.7);
}

/* Decorative elements inspired by metalwork */
.nav-decorative-element {
  position: absolute;
  background: linear-gradient(135deg, rgba(183, 185, 186, 0.15), rgba(100, 100, 110, 0.05));
  z-index: 0;
}

.left-element {
  top: 15px;
  left: 30%;
  width: 30px;
  height: 30px;
  clip-path: polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%);
  transform: rotate(45deg);
  opacity: 0.3;
}

.right-element {
  bottom: 15px;
  right: 30%;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: radial-gradient(circle, rgba(183, 185, 186, 0.2) 0%, rgba(100, 100, 110, 0.05) 70%);
  opacity: 0.2;
}

/* Scrolled state */
.navbar-scrolled {
  height: 55px;
  background-color: rgba(29, 30, 38, 0.95);
  backdrop-filter: blur(15px);
  -webkit-backdrop-filter: blur(15px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.4);
}

.navbar-scrolled .nav-backdrop {
  background: linear-gradient(90deg, 
    rgba(29, 30, 38, 0.95), 
    rgba(42, 43, 56, 0.95), 
    rgba(29, 30, 38, 0.95)
  );
}

/* Navigation lists */
.nav-list {
  display: flex;
  padding: 1rem 0;
  z-index: 3;
  flex-wrap: nowrap;
  gap: 1rem;
  
  @media (max-width: 768px) {
    display: none;
  }
}

.left-nav {
  justify-content: space-around; /* Right-justify against logo */
  margin-right: 0; /* Remove right margin to push against logo */
  margin-left: 1.5rem; /* Push away from menu */
}

.right-nav {
  justify-content: space-around; /* Left-justify against logo */
  margin-left: 0; /* Remove left margin to push against logo */
  margin-right: auto; /* Give space to social links */
}

/* Navigation items */
.nav-item-container {
  opacity: 0;
  transform: translateY(10px);
  animation: nav-item-entrance 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
  white-space: nowrap; /* Prevent text wrapping */
}

.nav-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  font-weight: 600;
  color: #ffffffe6;
  text-decoration: none;
  font-family: "PT Sans", sans-serif;
  position: relative;
  transition: color 0.3s ease;
  font-size: clamp(0.55rem, 0.9vw, 0.85rem); /* Responsive font sizing */
  padding: 0 0.25rem; /* Add some padding */
}

.nav-item-text {
  position: relative;
  z-index: 2;
  transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), color 0.3s ease;
}

.nav-item-underline {
  position: absolute;
  bottom: -5px;
  left: 50%;
  width: 0;
  height: 2px;
  background: #a22a21;
  transform: translateX(-50%);
  transition: width 0.3s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.nav-item:hover .nav-item-text {
  transform: translateY(-2px);
  color: #a22a21;
}

.nav-item:hover .nav-item-underline {
  width: 100%;
}

/* Animations */
@keyframes slide {
  0% {
    background-position: -1000px 0;
  }
  100% {
    background-position: 1000px 0;
  }
}

@keyframes slide-reverse {
  0% {
    background-position: 1000px 0;
  }
  100% {
    background-position: -1000px 0;
  }
}

@keyframes nav-item-entrance {
  0% {
    opacity: 0;
    transform: translateY(10px);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Add responsive adjustments for different screen sizes */
@media (max-width: 1200px) {
  .nav-item {
    font-size: clamp(0.75rem, 1vw, 0.9rem);
  }
  
  .left-section, .right-section {
    gap: 0.5rem;
  }
}

@media (max-width: 992px) {
  .nav-item {
    font-size: clamp(0.75rem, 0.9vw, 0.85rem);
  }
  
  .left-nav, .right-nav {
    gap: 0.5rem;
  }
}

/* Medium screens: Slightly reduce padding */
@media (max-width: 900px) {
  .left-section {
    padding-left: 0.5rem;
  }
  
  .right-section {
    padding-right: 0.5rem;
  }
}

/* For tablets and mobile: Adjust layout further */
@media (max-width: 768px) {
  .navbar-grid {
    grid-template-columns: auto 1fr auto;
  }
  
  .left-section {
    justify-content: flex-start;
  }
  
  .right-section {
    justify-content: flex-end;
  }
}
</style>
